export type DeleteAccountSubscriptionWarning =
  | { kind: "none" }
  | {
      /** A Stripe-billed plan: deletion drops access now; billing stops at period end. */
      kind: "stripe";
      title: string;
      body: string;
      actionLabel: string;
    }
  | {
      /** An App Store / Play plan: deleting the account does NOT stop the store billing. */
      kind: "store";
      title: string;
      body: string;
      actionLabel: string;
    };

/**
 * What the delete-account page must say about the user's subscription.
 *
 * Deleting an account is not the same as cancelling, and users mix them up (a
 * paying user deleted their account 15 days into a paid month thinking it was
 * "cancel", then re-registered minutes later). Anyone with a paid plan gets a
 * loud, specific warning plus a way out to the billing screen instead.
 *
 * Mirrors pinto/utils/appLogic/deleteAccount.logic.ts — keep the two in sync.
 */
export function resolveDeleteAccountSubscriptionWarning(input: {
  retailers?: string[] | null;
  subscriptionSrc?: string | null;
  customerId?: string | null;
}): DeleteAccountSubscriptionWarning {
  const hasPlan = Array.isArray(input.retailers) && input.retailers.length > 0;
  if (!hasPlan) return { kind: "none" };

  const source = input.subscriptionSrc?.trim().toLowerCase() || "";
  if (source.startsWith("revenuecat")) {
    return {
      kind: "store",
      title: "You have an active subscription",
      body:
        "Deleting your account does NOT cancel your App Store / Google Play subscription — you'll keep being billed until you cancel it in your store subscription settings. If you just want to stop paying, cancel there and keep your account.",
      actionLabel: "Manage subscription instead",
    };
  }

  return {
    kind: "stripe",
    title: "You have an active subscription",
    body:
      "Deleting is not the same as cancelling. If you only want to stop paying, cancel from Billing — you'll keep access until the end of the period you've already paid for. Deleting removes your access immediately and can't be undone.",
    actionLabel: "Manage subscription instead",
  };
}
